import React, { useEffect, useState } from "react";
import {
  FaHeart,
  FaShoppingCart,
  FaCheckCircle,
  FaClipboardList,
  FaListUl,
} from "react-icons/fa";
import { useMyContext } from "../Context/MyContext";
import OrdersTable from "./OrdersTable";
import { v4 as uuidv4 } from "uuid";

const BottomNav = () => {
  const {
    statusFilter,
    setStatusFilter,
    setFilterCategory,
    showOrdersModal,
    setShowOrdersModal,
    currentUser,
    isAdmin,
  } = useMyContext();

  const [activeTab, setActiveTab] = useState("all");
  const [visitorId, setVisitorId] = useState(null);

  const normalize = (value) =>
    (value || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  useEffect(() => {
    let storedVisitorId = localStorage.getItem("visitorId");

    if (!storedVisitorId) {
      storedVisitorId = uuidv4();
      localStorage.setItem("visitorId", storedVisitorId);
    }

    setVisitorId(storedVisitorId);
  }, []);

  /*
   * Keep the highlighted bottom-navigation tab synchronized
   * with the actual statusFilter stored in MyContext.
   */
  useEffect(() => {
    const normalizedFilter = normalize(statusFilter);

    if (normalizedFilter === "available") {
      setActiveTab("available");
    } else if (normalizedFilter === "preorder") {
      setActiveTab("preorder");
    } else if (normalizedFilter === "wishlist") {
      setActiveTab("wishlist");
    } else {
      setActiveTab("all");
    }
  }, [statusFilter]);

  /*
   * Keep Orders highlighted while its modal is open.
   */
  useEffect(() => {
    if (showOrdersModal) {
      setActiveTab("orders");
      return;
    }

    const normalizedFilter = normalize(statusFilter);

    if (normalizedFilter === "available") {
      setActiveTab("available");
    } else if (normalizedFilter === "preorder") {
      setActiveTab("preorder");
    } else if (normalizedFilter === "wishlist") {
      setActiveTab("wishlist");
    } else {
      setActiveTab("all");
    }
  }, [showOrdersModal, statusFilter]);

  const scrollToProducts = () => {
    const target = document.getElementById("cookingitems");

    if (!target) return;

    const offset = 80;
    const top =
      target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  const applyProductFilter = (filterValue) => {
    setShowOrdersModal(false);

    /*
     * Clear category filtering first so a previous category does not
     * prevent products from appearing under Available or Pre-Order.
     */
    setFilterCategory("");

    /*
     * Apply one consistent status value.
     */
    setStatusFilter(filterValue);

    /*
     * Scroll after React receives the new filter state.
     */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollToProducts();
      });
    });
  };

  const handleTabClick = (tab) => {
    if (tab === "orders") {
      setActiveTab("orders");
      setShowOrdersModal(true);
      return;
    }

    if (tab === "available") {
      setActiveTab("available");
      applyProductFilter("Available");
      return;
    }

    if (tab === "preorder") {
      setActiveTab("preorder");
      applyProductFilter("Pre-Order");
      return;
    }

    if (tab === "wishlist") {
      setActiveTab("wishlist");
      applyProductFilter("Wishlist");
      return;
    }

    setActiveTab("all");
    applyProductFilter("All");
  };

  const closeOrdersModal = () => {
    setShowOrdersModal(false);

    const normalizedFilter = normalize(statusFilter);

    if (normalizedFilter === "available") {
      setActiveTab("available");
    } else if (normalizedFilter === "preorder") {
      setActiveTab("preorder");
    } else if (normalizedFilter === "wishlist") {
      setActiveTab("wishlist");
    } else {
      setActiveTab("all");
    }
  };

  const tabs = [
    {
      id: "orders",
      label: isAdmin ? "Orders" : "My Orders",
      icon: FaClipboardList,
    },
    {
      id: "preorder",
      label: "Pre-Order",
      icon: FaShoppingCart,
    },
    {
      id: "available",
      label: "Available",
      icon: FaCheckCircle,
    },
    {
      id: "all",
      label: "All",
      icon: FaListUl,
    },
    {
      id: "wishlist",
      label: "Wishlist",
      icon: FaHeart,
    },
  ];

  return (
    <>
      {/* Fixed Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white shadow-[0_-4px_18px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex w-full items-stretch justify-between">
          {tabs.map(({ id, label, icon: Icon }) => {
            const isActive = activeTab === id;

            return (
              <button
                key={id}
                type="button"
                onClick={() => handleTabClick(id)}
                aria-label={label}
                aria-pressed={isActive}
                className={`group relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 px-1 py-2 transition-all duration-200 ${
                  isActive
                    ? "bg-[#f7f0ff] text-[#7b2cbf]"
                    : "text-gray-500 hover:bg-[#faf7ff] hover:text-[#7b2cbf]"
                }`}
              >
                {isActive && (
                  <span className="absolute left-1/2 top-0 h-1 w-10 -translate-x-1/2 rounded-b-full bg-[#45a864]" />
                )}

                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 ${
                    isActive
                      ? "bg-[#7b2cbf] text-white shadow-md"
                      : "bg-transparent group-hover:bg-[#f4eaff]"
                  }`}
                >
                  <Icon className="text-lg" />
                </span>

                <span
                  className={`max-w-full truncate text-[10px] font-semibold sm:text-xs ${
                    isActive ? "text-[#7b2cbf]" : "text-gray-500"
                  }`}
                >
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Orders Modal */}
      {showOrdersModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-5"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeOrdersModal();
            }
          }}
        >
          <div className="relative max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
              <div>
                <h2 className="text-xl font-extrabold text-gray-800 sm:text-2xl">
                  {isAdmin ? "CHAREX Orders" : "My CHAREX Orders"}
                </h2>

                <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                  {currentUser
                    ? "View and manage your CHAREX order information."
                    : "Sign in to view your CHAREX orders."}
                </p>
              </div>

              <button
                type="button"
                onClick={closeOrdersModal}
                aria-label="Close orders"
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#f7f0ff] text-xl font-bold text-[#7b2cbf] transition-colors duration-200 hover:bg-[#7b2cbf] hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-2 sm:p-4">
              <OrdersTable />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNav;